import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { setBeneficiaryDataAndRedirectToEditBeneficiaryPage } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/beneficiary-zone/redirect-to-edit-beneficiary.utils';
import { BeneficiaryFormService } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-form.service';
import { FormID } from '@finxone-platform/form-action';
import {
  ACCOUNTTYPE,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { FormActionState } from '../../../state/form-submision.state';
import { redirectToPage } from '../cta-button-actions.utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Edits the beneficiary based on the account type and redirects to the appropriate page.
 *
 * @param widgetProperties - The properties of the widget, including navigation details.
 * @param injector - The Angular injector used to retrieve services.
 *
 * This function retrieves the current form action state to determine the beneficiary's account type.
 * It sets the navigation URL in the widget properties based on whether the account type is personal
 * or business, and then redirects to the corresponding page using the router.
 */
export function editBeneficiary(widgetProperties: BaseWidgetProperties, injector: Injector) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const beneficiaryType = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
    ?.accountType;
  const beneficiaryCurrency = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
    ?.currency;
  const beneficiaryCountry = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
    ?.destinationIdentifier?.country;
  const newWidgetProps = { ...widgetProperties };
  if (beneficiaryCurrency === 'GBP' && beneficiaryCountry === 'GB') {
    if (beneficiaryType === ACCOUNTTYPE.PERSONAL) {
      newWidgetProps['urlToNavigate'] = 'edit-beneficiary-personal';
      redirectToPage(router, newWidgetProps, injector);
    } else {
      newWidgetProps['urlToNavigate'] = 'edit-beneficiary-business';
      redirectToPage(router, newWidgetProps, injector);
    }
  } else {
    newWidgetProps['urlToNavigate'] = 'edit-beneficiary-intl';
    setBeneficiaryDataAndRedirectToEditBeneficiaryPage(newWidgetProps, injector);
  }
}

export function editBeneficiaryUnified(widgetProperties: BaseWidgetProperties, injector: Injector) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const workflowFormsService = injector.get(WorkflowFormsService);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const beneficiaryFomService = injector.get(BeneficiaryFormService);
  const formActionStateData = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;

  // create the bene form here on workflow form service
  const beneficiaryForm = workflowFormsService.getFormPart(FormID.BENEFICIARY_FORM);
  if (beneficiaryForm) {
    workflowFormsService.patchFormValues(FormID.BENEFICIARY_FORM, {
      type: formActionStateData.accountType,
      country: formActionStateData.destinationIdentifier?.country || undefined,
      currency: formActionStateData.currency || undefined,
      // paymentRail: undefined,
      accountDetails: {
        nickName: formActionStateData.nickName || undefined,
        fullName: formActionStateData.name || undefined,
        accountNumber: formActionStateData.destinationIdentifier.accountNumber || undefined,
        sortCode: formActionStateData.destinationIdentifier.sortCode || undefined,
        iban: formActionStateData.destinationIdentifier.iban || undefined,
        BIC: formActionStateData.destinationIdentifier.bic || undefined,
        aba: formActionStateData.destinationIdentifier.aba || undefined,
        rtnCanada: formActionStateData.destinationIdentifier.rtnCanada || undefined,
        clabe: formActionStateData.destinationIdentifier.clabe || undefined,
        bsbCode: formActionStateData.destinationIdentifier.bsbCode || undefined,
        ifsc: formActionStateData.destinationIdentifier.ifsc || undefined,
        cnaps: formActionStateData.destinationIdentifier.cnaps || undefined,
        bankId: formActionStateData.destinationIdentifier.bankId || undefined,
        bankName: formActionStateData.destinationIdentifier.bankName || undefined,
        bankCode: formActionStateData.destinationIdentifier.bankCode || undefined,
        branchCode: formActionStateData.destinationIdentifier.branchCode || undefined,
      },
      beneficiaryAddress: formActionStateData.addressLine1
        ? {
            addressLine1: formActionStateData.addressLine1 || undefined,
            addressLine2: formActionStateData.addressLine2 || undefined,
            addressLine3: formActionStateData.addressLine3 || undefined,
            addressLine4: formActionStateData.addressLine4 || undefined,
            city: formActionStateData.city || undefined,
            stateProvinceRegion: formActionStateData.state || undefined,
            postalCode: formActionStateData.postCode || undefined,
            country: formActionStateData.country || undefined,
          }
        : undefined,
      id: formActionStateData.bid || undefined,
    });
  }
  if (widgetProperties?.actionNavigationConfigurations) {
    handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
  } else {
    redirectToPage(router, widgetProperties, injector);
  }
}
